import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/api-client'
import { Button } from '@/components/ui/Button'
import { Card, CardContent } from '@/components/ui/Card'
import { Loader2, Upload, Trash2, Star, X } from 'lucide-react'
import { useDropzone } from 'react-dropzone'
import { useDashboardEmbedded } from '@/hooks/useDashboardEmbedded'

interface GalleryImage {
  id: string
  url: string
  thumbnail_url: string
  caption: string | null
  alt_text: string | null
  is_featured: boolean
  sort_order: number
}

export default function GalleryManager() {
  const { t } = useTranslation('dashboard')
  const embedded = useDashboardEmbedded()
  const queryClient = useQueryClient()
  const [uploadingCount, setUploadingCount] = useState(0)
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null)

  // Fetch gallery images
  const { data: images = [], isLoading } = useQuery<GalleryImage[]>({
    queryKey: ['gallery'],
    queryFn: async () => {
      const response = await apiClient.get<{ data: GalleryImage[] }>('/user/gallery')
      return response.data.data
    },
  })

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      setUploadingCount(files.length)
      const formData = new FormData()
      files.forEach((file) => formData.append('images[]', file))
      
      const response = await apiClient.post('/user/gallery', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery'] })
    },
    onSettled: () => {
      setUploadingCount(0)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/user/gallery/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery'] })
      setSelectedImage(null)
    },
  })

  // Set featured mutation
  const featuredMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.put('/user/gallery/featured', { id })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery'] })
    },
  })

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        uploadMutation.mutate(acceptedFiles)
      }
    },
    [uploadMutation]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.gif'],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    maxFiles: 5,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8">
      {!embedded && (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('gallery.title')}</h1>
          <p className="text-gray-500">{t('gallery.subtitle')}</p>
        </div>
      )}

      {/* Upload Area */}
      <Card className="mb-8">
        <CardContent className="pt-6">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input {...getInputProps()} />
            {uploadingCount > 0 ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <span>{t('gallery.uploading', { count: uploadingCount })}</span>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">
                  {t('gallery.uploadPrompt')}
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  {t('gallery.uploadConstraints')}
                </p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Image Count */}
      <div className="mb-4">
        <p className="text-sm text-gray-500">
          {t('gallery.imageCount', { count: images.length })}
        </p>
      </div>

      {/* Gallery Grid */}
      {images.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image) => (
            <div
              key={image.id}
              className={`relative group rounded-lg overflow-hidden bg-gray-100 aspect-square ${
                image.is_featured ? 'ring-2 ring-primary' : ''
              }`}
            >
              <img
                src={image.thumbnail_url || image.url}
                alt={image.alt_text || image.caption || ''}
                className="w-full h-full object-cover"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute inset-0 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedImage(image)}
                    className="p-2 bg-white rounded-full hover:bg-gray-100"
                  >
                    <span className="sr-only">{t('gallery.edit')}</span>
                    <span className="text-lg">✏️</span>
                  </button>
                  {!image.is_featured && (
                    <button
                      type="button"
                      onClick={() => featuredMutation.mutate(image.id)}
                      className="p-2 bg-white rounded-full hover:bg-gray-100"
                      title={t('gallery.setAsFeatured')}
                    >
                      <Star className="w-4 h-4 text-gray-600" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => deleteMutation.mutate(image.id)}
                    className="p-2 bg-white rounded-full hover:bg-red-100"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>

                {/* Featured badge */}
                {image.is_featured && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-primary text-white text-xs rounded">
                    {t('gallery.featured')}
                  </div>
                )}
              </div>

              {/* Caption */}
              {image.caption && (
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                  <p className="text-white text-sm truncate">{image.caption}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-400">
          <p>{t('gallery.noImages')}</p>
        </div>
      )}

      {/* Edit Modal */}
      {selectedImage && (
        <EditImageModal
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
          onSave={async (data) => {
            const imageId = selectedImage.id
            await apiClient.put(`/user/gallery/${imageId}`, data)
            queryClient.invalidateQueries({ queryKey: ['gallery'] })
            setSelectedImage(null)
          }}
        />
      )}
    </div>
  )
}

interface EditImageModalProps {
  image: GalleryImage
  onClose: () => void
  onSave: (data: { caption?: string; alt_text?: string }) => Promise<void>
}

function EditImageModal({ image, onClose, onSave }: EditImageModalProps) {
  const { t } = useTranslation('dashboard')
  const [caption, setCaption] = useState(image.caption || '')
  const [altText, setAltText] = useState(image.alt_text || '')
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await onSave({ caption: caption || undefined, alt_text: altText || undefined })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">{t('gallery.editModal.title')}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          <img
            src={image.thumbnail_url || image.url}
            alt=""
            className="w-full h-48 object-contain rounded-lg bg-gray-100 mb-4"
          />

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('gallery.editModal.caption')}
              </label>
              <input
                type="text"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder={t('gallery.editModal.captionPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('gallery.editModal.altText')}
              </label>
              <input
                type="text"
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder={t('gallery.editModal.altTextPlaceholder')}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('gallery.editModal.altTextHint')}
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t">
          <Button variant="outline" onClick={onClose}>
            {t('gallery.editModal.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('gallery.editModal.saving') : t('gallery.editModal.saveChanges')}
          </Button>
        </div>
      </div>
    </div>
  )
}
